import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { IconLock, useApi, useOrg, unwrap, useResource } from "@backcoach/ui";
import type { Schemas } from "@backcoach/ui";

type OrgSettings = Schemas["OrgSettings"];

/**
 * Org defaults, the policy version, leaver removal and the audit log. The
 * headcount is the sponsor's own number and is labelled as theirs on the
 * dashboard. Leaver removal answers every address the same way.
 */
export function OrgSettingsScreen({ orgId }: { orgId: string }) {
  const api = useApi();
  const org = useOrg(orgId);
  const policies = useResource(async (a) => unwrap(await a.GET("/v1/orgs/{orgId}/policies", { params: { path: { orgId } } })), [orgId]);
  const audit = useResource(async (a) => unwrap(await a.GET("/v1/orgs/{orgId}/audit", { params: { path: { orgId } } })), [orgId]);
  const [form, setForm] = useState<OrgSettings | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [leaver, setLeaver] = useState("");
  const [leaverDone, setLeaverDone] = useState(false);

  useEffect(() => {
    if (org.data && !form) setForm(org.data.settings);
  }, [org.data, form]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    unwrap(await api.PUT("/v1/orgs/{orgId}/settings", { params: { path: { orgId } }, body: form }));
    setSaved("Saved. A policy change starts new plans from tomorrow.");
    org.reload();
    audit.reload();
    policies.reload();
  }

  async function removeLeaver(e: FormEvent) {
    e.preventDefault();
    await api.POST("/v1/orgs/{orgId}/leavers", { params: { path: { orgId } }, body: { email: leaver } });
    setLeaverDone(true);
    setLeaver("");
    audit.reload();
  }

  if (!org.data || !form) return <div className="bc-screen" aria-busy />;

  return (
    <div className="bc-screen bc-settings">
      <header className="bc-head">
        <p className="bc-kicker">Organisation</p>
        <h1>{org.data.name}</h1>
        <p className="bc-sub">
          <span className="bc-chip bc-chip-quiet">Domains: {org.data.domains.join(", ")}</span>
          <span className="bc-chip bc-chip-quiet">{org.data.status}</span>
        </p>
      </header>

      <form className="bc-block" onSubmit={(e) => void save(e)}>
        <div className="bc-block-head">
          <h2>Defaults for new joiners</h2>
        </div>
        <div className="set-row">
          <span className="set-label">Working hours</span>
          <span className="set-control">
            <input type="time" value={form.workStart} onChange={(e) => setForm({ ...form, workStart: e.target.value })} />
            <span className="set-and">to</span>
            <input type="time" value={form.workEnd} onChange={(e) => setForm({ ...form, workEnd: e.target.value })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">Timezone</span>
          <span className="set-control">
            <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">
            People in scope
            <span className="set-sub">Your number. It is the top of the adoption funnel and is shown as yours.</span>
          </span>
          <span className="set-control">
            <input type="number" min={0} value={form.headcountInScope} onChange={(e) => setForm({ ...form, headcountInScope: Number(e.target.value) })} />
          </span>
        </div>
        <div className="set-row">
          <span className="set-label">
            Prompt policy
            <span className="set-sub">Changing it starts new plans from tomorrow and is written to the audit log.</span>
          </span>
          <span className="set-control">
            <select value={form.policyVersion} onChange={(e) => setForm({ ...form, policyVersion: e.target.value })}>
              {(policies.data ?? []).map((p) => (
                <option key={p.version} value={p.version}>
                  {p.version}: {p.summary}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div className="set-actions">
          <button type="submit" className="bc-btn">
            Save
          </button>
          {saved && <span className="set-state">{saved}</span>}
        </div>
      </form>

      <form className="bc-block" onSubmit={(e) => void removeLeaver(e)}>
        <div className="bc-block-head">
          <IconLock size={16} />
          <h2>Someone has left</h2>
        </div>
        <p className="bc-note">
          Ask for an account to be closed by email address. The response is the same whether or not that address has ever enrolled, so this
          screen cannot tell you who has. The person is emailed first and has seven days.
        </p>
        <div className="set-row">
          <span className="set-label">Work email</span>
          <span className="set-control">
            <input type="email" required value={leaver} onChange={(e) => setLeaver(e.target.value)} />
            <button type="submit" className="bc-btn bc-btn-quiet">
              Request removal
            </button>
          </span>
        </div>
        {leaverDone && <p className="bc-note">Requested. If that address has an account, it closes in seven days.</p>}
      </form>

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>Audit log</h2>
        </div>
        {(audit.data ?? []).length === 0 && <p className="bc-note">Nothing yet.</p>}
        <ul className="notreported">
          {(audit.data ?? []).map((a) => (
            <li key={a.id}>
              <b>{a.action}</b> {a.detail ?? ""} <span className="set-sub">{a.actor}, {new Date(a.at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
